import React, { useEffect, useMemo, useState } from 'react';
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import toast from "react-hot-toast";
import { Calendar, Clock, Users, Zap, CalendarClock, Link2, Code2, Copy, Check } from 'lucide-react';
import { Textarea } from "../components/ui/textarea";
import Box from '@mui/material/Box';
import Header from '../../common/header/Header';
import Breadcrumb from '../../common/header/Breadcrumb';
import config_settings from '../../config/config';
import { apiCall } from '../../utils/action';

export const DEFAULT_SETTINGS = {
  scheduler_url: 'https://portal.palmtech.com/1234',
  display_inspector_names: true,
  allow_sameday_scheduling: true,
  nextday_cutoff: true,
  cutoff_time: '18:00',
  mark_as_scheduled: true,
  start_time: '08:00',
  end_time: '18:00',
};

const withDefaultSchedulerUrl = (schedulerUrl) => ({
  ...DEFAULT_SETTINGS,
  scheduler_url: schedulerUrl || DEFAULT_SETTINGS.scheduler_url,
});

const getCurrentEnvConfig = () => {
  const hostWithPort = window.location.host;
  const hostOnly = window.location.hostname;
  return config_settings?.[hostWithPort] || config_settings?.[hostOnly] || {};
};

const constructSchedulerUrl = (portalUrl, companyName, companyKey) => {
  const base = (portalUrl || "").replace(/\/+$/, "");
  const namePart = encodeURIComponent((companyName || "").trim());
  const keyPart = String(companyKey || "").trim();
  if (!base || !namePart || !keyPart) return "";
  return `${base}/${namePart}/online-scheduler/${keyPart}`;
};

const normalizeTimeValue = (value, fallback) => {
  if (typeof value !== "string" || !value.trim()) return fallback;
  const trimmed = value.trim();
  const hhmm = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!hhmm) return fallback;
  const hour = Math.min(23, Math.max(0, Number(hhmm[1])));
  const minute = Math.min(59, Math.max(0, Number(hhmm[2])));
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const normalizeSettings = (raw = {}) => ({
  scheduler_url: raw.scheduler_url || raw.schedulerUrl || DEFAULT_SETTINGS.scheduler_url,
  display_inspector_names:
    typeof raw.display_inspector_names === "boolean"
      ? raw.display_inspector_names
      : raw.display_inspector_names === 1 || raw.displayInspectorNames === true,
  allow_sameday_scheduling:
    typeof raw.allow_sameday_scheduling === "boolean"
      ? raw.allow_sameday_scheduling
      : raw.allow_sameday_scheduling === 1 || raw.allowSameDayScheduling === true,
  nextday_cutoff:
    typeof raw.nextday_cutoff === "boolean"
      ? raw.nextday_cutoff
      : raw.nextday_cutoff === 1 || raw.blockNextDayAfterCutoff === true,
  cutoff_time: normalizeTimeValue(
    raw.cutoff_time || raw.nextDayCutoffTime,
    DEFAULT_SETTINGS.cutoff_time
  ),
  mark_as_scheduled:
    typeof raw.mark_as_scheduled === "boolean"
      ? raw.mark_as_scheduled
      : raw.mark_as_scheduled === 1 || raw.autoScheduleOnConfirm === true,
  start_time: normalizeTimeValue(
    raw.start_time || raw.scheduleStartTime,
    DEFAULT_SETTINGS.start_time
  ),
  end_time: normalizeTimeValue(
    raw.end_time || raw.scheduleEndTime,
    DEFAULT_SETTINGS.end_time
  ),
});

const ConfigurationPage = () => {
  const envConfig = useMemo(() => getCurrentEnvConfig(), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [settings, setSettings] = useState(() => {
    const companyId = sessionStorage.getItem("company_id");
    const companyName = sessionStorage.getItem("company_name");
    const companyKey = sessionStorage.getItem("company_key") || companyId;
    const portalUrl = getCurrentEnvConfig()?.PORTAL_URL;
    const computedSchedulerUrl = constructSchedulerUrl(portalUrl, companyName, companyKey);
    const saved = localStorage.getItem('booking_config');
    if (!saved) return withDefaultSchedulerUrl(computedSchedulerUrl);
    return normalizeSettings(JSON.parse(saved));
  });

  const [copiedField, setCopiedField] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [showResetDialog, setShowResetDialog] = useState(false);

  useEffect(() => {
    const companyId = sessionStorage.getItem("company_id");
    const companyName = sessionStorage.getItem("company_name");
    const companyKey = sessionStorage.getItem("company_key") || companyId;
    const portalUrl = envConfig?.PORTAL_URL;
    if (!companyId) return;

    apiCall(
      (response) => {
        const responseData = response?.data?.data;
        const schedulerUrl = constructSchedulerUrl(portalUrl, companyName, companyKey);
        if (responseData == null) {
          setSettings(withDefaultSchedulerUrl(schedulerUrl));
          return;
        }
        const apiSettings = normalizeSettings(responseData);
        setSettings({
          ...apiSettings,
          scheduler_url: schedulerUrl,
        });
      },
      { company_id: companyId },
      "GET_SCHEDULER_SETTINGS"
    );
  }, [envConfig]);

  // Initialize Pendo using values already available in sessionStorage
  useEffect(() => {
    const companyId = sessionStorage.getItem("company_id");
    const uuId = sessionStorage.getItem("user_id");
    const roleId = sessionStorage.getItem("role_id");
    if (!companyId || !uuId || typeof window === "undefined" || !window.pendo) return;

    const userName = sessionStorage.getItem("user_name");
    const visitorName = userName && userName !== "null" ? userName : "";
    const userEmail = sessionStorage.getItem("user_email") || "";
    const roleLabel =
      roleId === "5" ? "Owner" : roleId === "6" ? "Admin" : roleId === "7" ? "Inspector" : "";

    window.pendo.initialize({
      visitor: {
        id: uuId,
        Name: visitorName,
        Address: "",
        Email: userEmail,
        Role: roleLabel,
      },
      account: {
        company_id: companyId,
        ParentCompany: "PalmTech",
      },
    });
  }, []);

  const saveSettings = (newSettings) => {
    const companyId = sessionStorage.getItem("company_id");
    const userId = sessionStorage.getItem("user_id");
    if (!companyId || !userId) return;

    setSaveStatus('saving');
    const payload = {
      company_id: Number(companyId),
      start_time: newSettings.start_time,
      end_time: newSettings.end_time,
      display_inspector_names: newSettings.display_inspector_names ? 1 : 0,
      allow_sameday_scheduling: newSettings.allow_sameday_scheduling ? 1 : 0,
      nextday_cutoff: newSettings.nextday_cutoff ? 1 : 0,
      cutoff_time: newSettings.cutoff_time,
      mark_as_scheduled: newSettings.mark_as_scheduled ? 1 : 0,
      scheduler_url: newSettings.scheduler_url,
      user_id: Number(userId),
    };

    apiCall(
      (response) => {
        if (response?.data?.success) {
          localStorage.setItem('booking_config', JSON.stringify(newSettings));
          setSaveStatus('saved');
          toast.success("Your changes have been saved.");
          setTimeout(() => setSaveStatus(null), 2000);
        } else {
          setSaveStatus('error');
          toast.error(response?.data?.message || "Unable to store scheduler settings.");
        }
      },
      payload,
      "STORE_SCHEDULER_SETTINGS"
    );
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    if (
      (key === "start_time" || key === "end_time") &&
      newSettings.start_time >= newSettings.end_time
    ) {
      toast.error("Start time must be before end time.");
      return;
    }

    saveSettings(newSettings);
  };

  const iframeCode = `<iframe src="${settings.scheduler_url}" width="100%" height="800" frameborder="0" style="border: 0;" title="Online Scheduler"></iframe>`;

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleResetConfirm = () => {
    const companyId = sessionStorage.getItem("company_id");
    const companyName = sessionStorage.getItem("company_name");
    const companyKey = sessionStorage.getItem("company_key") || companyId;
    const portalUrl = envConfig?.PORTAL_URL;
    const fallbackUrl = constructSchedulerUrl(portalUrl, companyName, companyKey);
    const resetted = withDefaultSchedulerUrl(fallbackUrl);
    setSettings(resetted);
    localStorage.removeItem('booking_config');
    saveSettings(resetted);
    toast.success("All settings have been restored to defaults.");
    setShowResetDialog(false);
  };

  // Generate cutoff time options (every 30 min from 12PM to 11:30PM)
  const cutoffTimeOptions = [];
  for (let h = 12; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      const displayHour = h > 12 ? h - 12 : h;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const label = `${displayHour}:${m.toString().padStart(2, '0')} ${ampm}`;
      cutoffTimeOptions.push({ value, label });
    }
  }

  // Generate full-day time options (every 30 min from 12AM to 11:30PM)
  const allTimeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const label = `${displayHour}:${m.toString().padStart(2, '0')} ${ampm}`;
      allTimeOptions.push({ value, label });
    }
  }

  return (
    <Box className="page-background online-scheduler-root online-scheduler-configuration-page">
      <Header />
      <Breadcrumb />
      <div>
      <Box component="main" sx={{ px: { xs: '20px', sm: '20px', md: '80px', lg: '80px' }, pt: '25px', pb: '80px' }}>
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 style={{ fontSize: '22px', fontWeight: 600, color: '#3b3b3b', fontFamily: "'Open Sans', sans-serif" }}>Online Scheduler Configuration</h1>
              <p style={{ fontSize: '14px', color: '#333333', fontFamily: "'Open Sans', sans-serif" }}>Manage scheduling and booking behavior for the Online Scheduler</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Scheduler URL & Embed */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex gap-3">
              <Link2 className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <Label className="text-sm font-semibold text-foreground">Online Scheduler URL</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  The public link for your online scheduler. Share this URL with customers or use it to embed the scheduler on your website.
                </p>
                <div className="mt-3 flex gap-2">
                  <Textarea
                    readOnly
                    value={settings.scheduler_url}
                    rows={1}
                    className="flex-1 text-sm bg-gray-50 resize-none min-h-[40px] leading-6"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCopy(settings.scheduler_url, 'url')}
                    className="gap-2 shrink-0"
                  >
                    {copiedField === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copiedField === 'url' ? 'Copied' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Code2 className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-semibold text-foreground">Embed Code (iframe)</Label>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Paste this snippet into your website's HTML to embed the scheduler.
                  </p>
                  <div className="mt-3 space-y-2">
                    <Textarea
                      readOnly
                      value={iframeCode}
                      rows={3}
                      className="font-mono text-sm bg-gray-50"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(iframeCode, 'iframe')}
                      className="gap-2"
                    >
                      {copiedField === 'iframe' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copiedField === 'iframe' ? 'Copied' : 'Copy embed code'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Window */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex gap-3">
              <CalendarClock className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1">
                <Label className="text-sm font-semibold text-foreground">Schedule Appointment Window</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Only allow appointments to be scheduled between these times each day.
                </p>
                <div className="mt-3 flex flex-wrap gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">After (Start Time)</Label>
                    <Select
                      value={settings.start_time}
                      onValueChange={(val) => updateSetting('start_time', val)}
                    >
                      <SelectTrigger className="w-40 bg-white border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allTimeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Before (End Time)</Label>
                    <Select
                      value={settings.end_time}
                      onValueChange={(val) => updateSetting('end_time', val)}
                    >
                      <SelectTrigger className="w-40 bg-white border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {allTimeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {settings.start_time >= settings.end_time && (
                  <p className="text-xs text-destructive mt-2">Start time must be before end time.</p>
                )}
              </div>
            </div>
          </div>

          {/* Inspector Names in Slots */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <Users className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <Label className="text-sm font-semibold text-foreground">Display Inspector Names in Appointment Slots</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Show which inspector(s) are available for each time slot when booking.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.display_inspector_names}
                onCheckedChange={(val) => updateSetting('display_inspector_names', val)}
              />
            </div>
          </div>

          {/* Same Day Scheduling */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <Label className="text-sm font-semibold text-foreground">Allow Same-Day Scheduling</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Allow customers to book inspections for today's date.
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.allow_sameday_scheduling}
                onCheckedChange={(val) => updateSetting('allow_sameday_scheduling', val)}
              />
            </div>
          </div>

          {/* Next Day Cutoff */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <Label className="text-sm font-semibold text-foreground">Next-Day Cutoff</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Prevent next-day inspections from being booked after a specific time today.
                  </p>
                  {settings.nextday_cutoff && (
                    <div className="mt-3">
                      <Label className="text-xs text-muted-foreground mb-1.5 block">Cutoff Time</Label>
                      <Select
                        value={settings.cutoff_time}
                        onValueChange={(val) => updateSetting('cutoff_time', val)}
                      >
                        <SelectTrigger className="w-40 bg-white border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {cutoffTimeOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              <Switch
                checked={settings.nextday_cutoff}
                onCheckedChange={(val) => updateSetting('nextday_cutoff', val)}
              />
            </div>
          </div>

          {/* Auto Schedule */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <Zap className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <Label className="text-sm font-semibold text-foreground">Automatically Schedule Online Orders</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                  When enabled, any order booked through the online scheduler is automatically scheduled and moved from ‘Unscheduled’ to ‘Scheduled/Confirmed’, removing the need for manual confirmation by inspectors.                  </p>
                </div>
              </div>
              <Switch
                checked={settings.mark_as_scheduled}
                onCheckedChange={(val) => updateSetting('mark_as_scheduled', val)}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <Button variant="outline" onClick={() => setShowResetDialog(true)} className="text-muted-foreground">
            Reset to Defaults
          </Button>

          {/* Reset Confirmation Dialog */}
          {showResetDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowResetDialog(false)} />
              <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
                <div className="flex items-start gap-3 mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Reset to Defaults</h3>
                    <p className="text-sm text-gray-600 mt-1">Are you sure you want to reset to default configurations?</p>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleResetConfirm} className="bg-[#239D80] hover:bg-[#239D80]/90 border-transparent text-white">
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Box>
      </div>
    </Box>
  );
};

export default ConfigurationPage;